import { useAddressModal, useAddressModalCallback } from "@shared/AddressModal";
// Use the person endpoint (no admin gate) rather than @shared/AddressModal's
// business_settings variant, which is admin-only.
import { getSuggestedAddressCheck } from "../../../../PersonDetail/shared/api";
import { classifyStandardizedAddress } from "./classifyStandardizedAddress";

const MODAL_TITLE = "Edit Property Address";

/**
 * Wires the shared address modals (addEdit / invalid / verify) to the
 * suggest_standardized_address flow for TDP Property Details.
 *
 * @param {Object} params
 * @param {Object} params.initAddress - current address in modal camelCase shape
 * @param {Function} params.onSave - persists a modal-shape address
 * @returns {{ openEditor: Function }}
 */
export const useAddressStandardization = ({ initAddress, onSave }) => {
  const addressModal = useAddressModal("addEdit");
  const invalidModal = useAddressModal("invalid");
  const verifyModal = useAddressModal("verify");
  const { setOnSubmit, setInvalidOnSubmit, setInvalidOnEdit, setVerifyOnSubmit, setVerifyOnEdit } =
    useAddressModalCallback();

  const handleAddEditSubmit = async (address) => {
    const { streetAddress, secondaryAddress, city, locality, postalCode, country } = address;
    const { res } = await getSuggestedAddressCheck({
      streetAddress,
      secondaryAddress,
      city,
      locality,
      postalCode,
      country,
    });
    addressModal.close();

    const result = classifyStandardizedAddress(res);

    if (result.type === "invalid") {
      setInvalidOnSubmit(() => onSave(address));
      setInvalidOnEdit(() => {
        setOnSubmit(handleAddEditSubmit);
        addressModal.open({ title: MODAL_TITLE, initAddress: address, hideAddressType: true });
        invalidModal.close();
      });
      invalidModal.open({ address });
    } else if (result.type === "verify") {
      setVerifyOnSubmit((chosen) => onSave(chosen));
      setVerifyOnEdit(() => {
        setOnSubmit(handleAddEditSubmit);
        addressModal.open({ title: MODAL_TITLE, initAddress: address, hideAddressType: true });
        verifyModal.close();
      });
      verifyModal.open({ originalAddress: address, suggestedAddress: result.suggestedAddress });
    } else {
      onSave(address);
    }
  };

  const openEditor = () => {
    setOnSubmit(handleAddEditSubmit);
    addressModal.open({ title: MODAL_TITLE, initAddress, hideAddressType: true });
  };

  return { openEditor };
};

export default useAddressStandardization;
